import * as React from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { QRCodeSVG } from 'qrcode.react';

import color from '../../style/color';
import { font } from '../../style/text';
import Button from '../common/Button';
import AuthCard from './AuthCard';

interface Props {
    title: string;
    subtitle: string;
    error?: string;
    secretLabel?: string;
    secret?: string;
    qrCodeValue?: string;
    codePlaceholder: string;
    code: string;
    onCodeChange: (code: string) => void;
    buttonLabel: string;
    cancelLabel: string;
    isLoading: boolean;
    onSubmit: () => void;
    onCancel: () => void;
}

export default function MfaCodeForm({
    title,
    subtitle,
    error,
    secretLabel,
    secret,
    qrCodeValue,
    codePlaceholder,
    code,
    onCodeChange,
    buttonLabel,
    cancelLabel,
    isLoading,
    onSubmit,
    onCancel,
}: Props) {
    const updateCode = (value: string) => onCodeChange(value.replace(/\D/g, '').slice(0, 6));
    return (
        <AuthCard>
            <Text style={styles.textTitle}>{title}</Text>
            <Text style={styles.textSubtitle}>{subtitle}</Text>
            {error ? (
                <View style={styles.containerErrorMessage}>
                    <Text style={styles.textErrorMessage}>{error}</Text>
                </View>
            ) : null}
            {qrCodeValue ? (
                <View style={styles.containerQrCode}>
                    <QRCodeSVG value={qrCodeValue} size={180} />
                </View>
            ) : null}
            {secretLabel && secret ? (
                <>
                    <Text style={styles.textSecretLabel}>{secretLabel}</Text>
                    <Text style={styles.textSecret}>{secret}</Text>
                </>
            ) : null}
            <View style={[code ? styles.bottomBorder : styles.bottomBorderInactive, styles.containerTextInput]}>
                <TextInput
                    style={styles.textInput}
                    placeholder={codePlaceholder}
                    value={code}
                    autoCorrect={false}
                    keyboardType="number-pad"
                    returnKeyType="done"
                    editable={!isLoading}
                    onChangeText={updateCode}
                    onSubmitEditing={onSubmit}
                    placeholderTextColor={color.frenchGray}
                />
            </View>
            <Button
                {...{ isLoading }}
                onPress={onSubmit}
                disabled={code.length !== 6}
                textStyle={styles.textButton}
                height={40}>
                {buttonLabel}
            </Button>
            <TouchableOpacity onPress={onCancel} disabled={isLoading}>
                <Text style={styles.textCancelButton}>{cancelLabel}</Text>
            </TouchableOpacity>
        </AuthCard>
    );
}

const styles = StyleSheet.create({
    containerQrCode: {
        alignItems: 'center',
        marginBottom: 24,
    },
    containerErrorMessage: {
        backgroundColor: color.fairPink,
        paddingVertical: 8,
        paddingHorizontal: 25,
        borderRadius: 10,
        marginBottom: 16,
    },
    containerTextInput: {
        flexDirection: 'row',
        marginHorizontal: 4,
        marginBottom: 24,
    },
    textTitle: {
        fontFamily: font.ambitSemiBold,
        fontSize: 20,
        color: color.codGray,
        marginBottom: 24,
    },
    textSubtitle: {
        fontFamily: font.ambitRegular,
        fontSize: 14,
        color: color.tundora,
        marginBottom: 24,
    },
    textErrorMessage: {
        color: color.radicalRed,
        fontFamily: font.ambitSemiBold,
        fontSize: 14,
        alignSelf: 'center',
        textAlign: 'center',
    },
    textSecretLabel: {
        fontFamily: font.ambitSemiBold,
        fontSize: 14,
        color: color.tundora,
        marginBottom: 8,
    },
    textSecret: {
        fontFamily: font.ambitRegular,
        fontSize: 16,
        color: color.tundora,
        marginBottom: 24,
    },
    textInput: {
        height: 35,
        fontFamily: font.ambitRegular,
        color: color.tundora,
        fontSize: 16,
        flexGrow: 1,
        outlineWidth: 0,
    },
    textButton: {
        fontSize: 14,
    },
    textCancelButton: {
        marginTop: 16,
        textAlign: 'center',
        fontFamily: font.ambitSemiBold,
        fontSize: 14,
        color: color.emerald,
    },
    bottomBorder: {
        borderBottomWidth: 1,
        borderBottomColor: color.tundora,
    },
    bottomBorderInactive: {
        borderBottomWidth: 1,
        borderBottomColor: color.frenchGray,
    },
});
